import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, X, Utensils, GlassWater, Flame } from "lucide-react";
import { toast } from "sonner";
import { empApi } from "@/lib/api";
import { formatTime12 } from "@/lib/format";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function buildMatrix(year, month) {
  const first = new Date(year, month - 1, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export default function EmployeeCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [days, setDays] = useState({});
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await empApi.get(`/employee/calendar?year=${year}&month=${month}`);
      setDays(data.days || {});
    } catch { toast.error("Could not load calendar"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [year, month]);

  async function openEvent(id) {
    try {
      const { data } = await empApi.get(`/employee/events/${id}`);
      setEvent(data);
    } catch { toast.error("Could not load event"); }
  }

  const matrix = useMemo(() => buildMatrix(year, month), [year, month]);
  const totalEvents = Object.values(days).reduce((a, l) => a + l.length, 0);
  const isToday = (d) => d && today.getFullYear() === year && today.getMonth() + 1 === month && today.getDate() === d;

  function go(delta) {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  }

  return (
    <div data-testid="employee-calendar-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Schedule</div>
        <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Catering Calendar</h1>
        <p className="text-white/50 mt-2 text-sm">{totalEvents} event{totalEvents !== 1 ? "s" : ""} in {MONTH_LABELS[month - 1]} {year}</p>
      </div>

      <div className="flex items-center justify-between bg-[#0e0e0e] border border-white/10 px-5 py-3 mb-4">
        <button onClick={() => go(-1)} className="p-2 text-white/70 hover:text-[#C9963F]" data-testid="emp-prev-month"><ChevronLeft size={18} /></button>
        <div className="flex items-center gap-3">
          <CalendarDays size={16} className="text-[#C9963F]" strokeWidth={1.5} />
          <h2 className="font-serif-brand text-2xl sm:text-3xl" data-testid="emp-month-label">{MONTH_LABELS[month - 1]} {year}</h2>
        </div>
        <button onClick={() => go(1)} className="p-2 text-white/70 hover:text-[#C9963F]" data-testid="emp-next-month"><ChevronRight size={18} /></button>
      </div>

      <div className="bg-[#0e0e0e] border border-white/10" data-testid="emp-calendar-grid">
        <div className="grid grid-cols-7 border-b border-white/10">
          {DAY_LABELS.map((d) => (
            <div key={d} className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#C9963F] text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {matrix.flat().map((d, idx) => {
            const evs = d ? (days[d] || []) : [];
            return (
              <div key={idx} className={`min-h-[100px] p-2 border-b border-r border-white/5 ${!d ? "bg-white/[0.01]" : ""}`}>
                {d && (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${isToday(d) ? "bg-[#C9963F] text-black px-1.5 py-0.5" : "text-white/80"}`}>{d}</span>
                      {evs.length > 0 && <span className="text-[9px] text-[#C9963F]">{evs.length}</span>}
                    </div>
                    <div className="space-y-1">
                      {evs.slice(0, 3).map((ev) => (
                        <button
                          key={ev.id}
                          onClick={() => openEvent(ev.id)}
                          data-testid={`emp-event-${ev.id}`}
                          className="w-full text-left flex items-center gap-1 text-[10px] text-white/80 bg-[#C9963F]/10 border-l-2 border-[#C9963F] px-1.5 py-0.5 truncate hover:bg-[#C9963F]/20"
                        >
                          <span className="inline-flex gap-1">
                            {ev.catering_food && <Utensils size={10} className="text-[#C9963F]" />}
                            {ev.catering_mocktail && <GlassWater size={10} className="text-[#C9963F]" />}
                            {ev.catering_hookah && <Flame size={10} className="text-[#C9963F]" />}
                          </span>
                          <span className="truncate">
                            {ev.event_time && <span className="text-white/50">{formatTime12(ev.event_time)} </span>}
                            {ev.client_name}
                          </span>
                        </button>
                      ))}
                      {evs.length > 3 && <div className="text-[10px] text-white/40">+{evs.length - 3} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {loading && <div className="mt-4 text-white/40 text-xs">Loading…</div>}

      {event && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEvent(null)}>
          <div className="bg-[#0e0e0e] border border-[#C9963F]/40 max-w-lg w-full p-6 sm:p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="emp-event-dialog">
            <button onClick={() => setEvent(null)} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={18} /></button>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Event Details</div>
            <h3 className="font-serif-brand text-2xl mb-4">{event.client_name}</h3>
            <div className="space-y-2 text-sm">
              <Row label="Date" value={new Date(event.event_date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })} />
              <Row label="Time" value={formatTime12(event.event_time) || "TBD"} />
              <Row label="Location" value={event.event_location || "—"} />
              <Row label="Guests" value={event.guest_count || "—"} />
              <Row label="Services" value={[
                event.catering_food && "Food",
                event.catering_mocktail && "Mocktail",
                event.catering_hookah && "Hookah",
              ].filter(Boolean).join(", ") || "—"} />
              {event.menu_description && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Menu</div>
                  <div className="text-sm text-white/80 whitespace-pre-wrap">{event.menu_description}</div>
                </div>
              )}
              {event.addons && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Add-ons</div>
                  <div className="text-sm text-white/80 whitespace-pre-wrap">{event.addons}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-white/5">
      <span className="text-[10px] uppercase tracking-wider text-white/50 shrink-0">{label}</span>
      <span className="text-white/90 text-right truncate">{value}</span>
    </div>
  );
}
